import { ChartData, ChartTypeRegistry } from 'chart.js';
import { AxisChartByDate, DataPointSetByDate } from '../chart.models';
import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { ChartBar } from 'common/components/charts/chart-bar';
import { ChartLine } from 'common/components/charts/chart-line';
import { getChartTypeAndRange } from '../chartOptionsSlice';
import { DateRangeTypes } from '../chartButtons/constants/RangeRoutes';
import { chartColours } from 'common/components/charts/chart-colours';

export const ChartStrategyDate = ({ chart }: { chart: AxisChartByDate }) => {
  const [selectedChartType, selectedChartRange] = useSelector(
    (state: RootState) => getChartTypeAndRange(state, chart.id)
  );

  const groupByDay = (dataPointSets: DataPointSetByDate[]) => {
    const days = [...new Set(dataPointSets.map((dp) => dp.dataPointDate))];

    const dataPointSetNames = [
      ...new Set(dataPointSets.map((dp) => dp.dataPointSetName))
    ];

    const chartData: ChartData<keyof ChartTypeRegistry> = {
      labels: days,
      datasets: dataPointSetNames.map((dps, i) =>
        toChartDataSet(dps, i, dataPointSets, days)
      )
    };

    return chartData;
  };

  const groupByWeek = (dataPointSets: DataPointSetByDate[]) => {
    const dataPointsByWeeks = dataPointSets.map(
      toDataPointSetWithWeeklyDateFormat
    );

    const weeks = [...new Set(dataPointsByWeeks.map((dp) => dp.dataPointDate))];

    const dataPointSetNames = [
      ...new Set(dataPointsByWeeks.map((dp) => dp.dataPointSetName))
    ];

    const dataPointsByWeeksAndCount = aggregateOnCountByDate(dataPointsByWeeks);

    const chartData: ChartData<keyof ChartTypeRegistry> = {
      labels: weeks,
      datasets: dataPointSetNames.map((dps, i) =>
        toChartDataSet(dps, i, dataPointsByWeeksAndCount, weeks)
      )
    };
    return chartData;

    function toDataPointSetWithWeeklyDateFormat(
      dataPointSet: DataPointSetByDate
    ) {
      const dataPointDate = moment(new Date(dataPointSet.dataPointDate));

      const currentWeekStart: string = dataPointDate
        .startOf('week')
        .format('MMMM - DD')
        .toLocaleString();
      const currentWeekEnd: string = dataPointDate
        .endOf('week')
        .format('DD')
        .toLocaleString();

      const dataPointSetWithMonthDate: DataPointSetByDate = {
        ...dataPointSet,
        dataPointDate: `${currentWeekStart}-${currentWeekEnd}`
      };
      return dataPointSetWithMonthDate;
    }
  };

  const groupByMonth = (dataPointSets: DataPointSetByDate[]) => {
    const dataPointSetsByMonths = dataPointSets.map((dps) =>
      toDataPointSetWithDateFormat(dps, 'MMMM-YYYY')
    );

    const months = [
      ...new Set(dataPointSetsByMonths.map((dp) => dp.dataPointDate))
    ];

    const dataPointSetNames = [
      ...new Set(dataPointSetsByMonths.map((dp) => dp.dataPointSetName))
    ];

    const dataPointsByMonthsAndCount = aggregateOnCountByDate(
      dataPointSetsByMonths
    );

    const chartData: ChartData<keyof ChartTypeRegistry> = {
      labels: months,
      datasets: dataPointSetNames.map((dps, i) =>
        toChartDataSet(dps, i, dataPointsByMonthsAndCount, months)
      )
    };

    return chartData;
  };

  const groupByYear = (dataPointSets: DataPointSetByDate[]) => {
    const dataPointSetsByYears = dataPointSets.map((dps) =>
      toDataPointSetWithDateFormat(dps, 'YYYY')
    );

    const years = [
      ...new Set(dataPointSetsByYears.map((dp) => dp.dataPointDate))
    ];

    const dataPointSetNames = [
      ...new Set(dataPointSetsByYears.map((dp) => dp.dataPointSetName))
    ];

    const dataPointsByYearsAndCount =
      aggregateOnCountByDate(dataPointSetsByYears);

    const chartData: ChartData<keyof ChartTypeRegistry> = {
      labels: years,
      datasets: dataPointSetNames.map((dps, i) =>
        toChartDataSet(dps, i, dataPointsByYearsAndCount, years)
      )
    };

    return chartData;
  };

  const groupByDate = useCallback(
    (dataPointSets: DataPointSetByDate[], dateRangeType: DateRangeTypes) => {
      switch (dateRangeType) {
        case DateRangeTypes.DAY:
          return groupByDay(dataPointSets);
        case DateRangeTypes.WEEK:
          return groupByWeek(dataPointSets);
        case DateRangeTypes.MONTH:
          return groupByMonth(dataPointSets);
        case DateRangeTypes.YEAR:
          return groupByYear(dataPointSets);
        default:
          throw Error(`Unknown date range type: ${dateRangeType}`);
      }
    },
    []
  );

  const groupedChartData = useMemo(() => {
    if (chart.dataPointSets.length > 0) {
      return groupByDate(chart.dataPointSets, selectedChartRange);
    }
  }, [chart.dataPointSets, groupByDate, selectedChartRange]);

  const tryRenderChart = () => {
    switch (selectedChartType) {
      case 'stackedBar':
        return (
          <ChartBar
            title={chart.title}
            data={groupedChartData as ChartData<'bar'>}
            isStackedX={true}
            isStackedY={true}
            yAxisFormat={chart.yAxisFormat}
            tooltipFormat={chart.tooltipFormat}
            xAxisTitle={chart.xAxisTitle}
            yAxisTitle={chart.yAxisTitle}
            shouldAnimate={true}
            key={`${chart.id}-bar`}
          />
        );
      case 'stackedLine':
        return (
          <ChartLine
            title={chart.title}
            data={groupedChartData as ChartData<'line'>}
            isStackedX={true}
            isStackedY={true}
            isLinearX={false}
            legendKeysToIgnore={[]}
            xAxisTitle={chart.xAxisTitle}
            yAxisTitle={chart.yAxisTitle}
            key={`${chart.id}-line`}
          />
        );
    }
  };

  return groupedChartData ? <>{tryRenderChart()}</> : null;
};

function toDataPointSetWithDateFormat(
  dataPointSet: DataPointSetByDate,
  dateFormat: string
) {
  const dataPointSetWithMonthDate: DataPointSetByDate = {
    ...dataPointSet,
    dataPointDate: moment(new Date(dataPointSet.dataPointDate)).format(
      dateFormat
    )
  };
  return dataPointSetWithMonthDate;
}

function aggregateOnCountByDate(dataPoints: DataPointSetByDate[]) {
  return dataPoints.reduce((all: DataPointSetByDate[], next) => {
    const existingDataSetDateAggregate = all.find(
      (dps) =>
        dps.dataPointDate === next.dataPointDate &&
        dps.dataPointSetName === next.dataPointSetName
    );

    if (existingDataSetDateAggregate) {
      existingDataSetDateAggregate.countByDate =
        existingDataSetDateAggregate.countByDate + next.countByDate;
      return all;
    } else {
      return [...all, next];
    }
  }, []);
}

// We use 'any' because ChartJs types can be awkward
function toChartDataSet(
  dataPointSetName: string,
  index: number,
  dataPointsByDateAndCount: DataPointSetByDate[],
  labels: string[]
) {
  const datePointsByDate = labels.map(
    (label) =>
      dataPointsByDateAndCount.find(
        (dps) =>
          dps.dataPointSetName === dataPointSetName &&
          dps.dataPointDate === label
      )?.countByDate ?? 0
  );

  const dataSet: any = {
    label: dataPointSetName,
    backgroundColor: chartColours[index],
    fill: true,
    data: datePointsByDate
  };

  return dataSet;
}
